import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  AssignV3Strategy,
  type AssignV3ActionMode,
  type AssignV3ActorAssigneeType,
} from "@redotech/redo-flows-common/rules/actions";
import {
  AssignmentTypeOption,
  type AssignOldestTicketAction,
  type AssignV3Action as AssignV3ActionType,
} from "@redotech/redo-flows-common/rules/schemas";
import { RuleActionType } from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { catchApiErrors } from "../../../../v2/db/error-handling";
import { buildUserOptions } from "./build-user-options";
import {
  defaultCurrentAssignmentTypesForMode,
  serializeCurrentAssignmentTypes,
} from "./current-assignment-types";

type AssignPoolActionConfig = AssignV3ActionType | AssignOldestTicketAction;

type ModeTag = AssignV3ActionMode["_tag"];

const modeOptions = () => [
  {
    id: "specific_users" as const,
    type: "text" as const,
    text: t("Specific users"),
    value: "specific_users" as ModeTag,
  },
  {
    id: "user_group" as const,
    type: "text" as const,
    text: t("User group"),
    value: "user_group" as ModeTag,
  },
  {
    id: "ai" as const,
    type: "text" as const,
    text: t("AI agent"),
    value: "ai" as ModeTag,
  },
  {
    id: "actor" as const,
    type: "text" as const,
    text: t("Trigger actor"),
    value: "actor" as ModeTag,
  },
  {
    id: "unassign" as const,
    type: "text" as const,
    text: t("Unassign"),
    value: "unassign" as ModeTag,
  },
];

const actorAssigneeTypeLabels = (): Record<
  AssignV3ActorAssigneeType,
  string
> => ({ USER: t("Human agent"), AI: t("AI agent") });

const actorAssigneeTypeOptions =
  (): BadgeRedoListItem<AssignV3ActorAssigneeType>[] =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (Object.keys(actorAssigneeTypeLabels()) as AssignV3ActorAssigneeType[]).map(
      (value) => ({
        id: value,
        type: "badge" as const,
        value,
        badge: { text: actorAssigneeTypeLabels()[value] },
      }),
    );

const currentAssignmentTypeOptions =
  (): BadgeRedoListItem<AssignmentTypeOption>[] => [
    {
      id: AssignmentTypeOption.NO_ASSIGNEE,
      type: "badge",
      value: AssignmentTypeOption.NO_ASSIGNEE,
      badge: { text: t("No assignee") },
    },
    {
      id: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
      type: "badge",
      value: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
      badge: { text: t("Support agent assigned") },
    },
    {
      id: AssignmentTypeOption.AI_AGENT_ASSIGNEE,
      type: "badge",
      value: AssignmentTypeOption.AI_AGENT_ASSIGNEE,
      badge: { text: t("AI agent assigned") },
    },
  ];

const strategyOptions = () => [
  {
    id: AssignV3Strategy.ROUND_ROBIN,
    type: "text" as const,
    text: t("Round robin"),
    value: AssignV3Strategy.ROUND_ROBIN,
  },
  {
    id: AssignV3Strategy.BALANCED,
    type: "text" as const,
    text: t("Balanced (fewest open tickets)"),
    value: AssignV3Strategy.BALANCED,
  },
];

export const AssignV3Action = memo(function AssignV3Action({
  action,
  onChange,
  editableFields,
}: {
  action: AssignPoolActionConfig;
  onChange: (updated: AssignPoolActionConfig) => void;
  /**
   * When omitted, all fields are editable (normal rule authoring). When
   * provided, only fields whose name is in the set are editable; others
   * render read-only. Used by the default-rule edit page to honour the
   * rule's template bindings.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const effectApi = useRequiredContext(EffectApiClientContext);

  const isEditable = (field: string) =>
    editableFields === undefined || editableFields.has(field);
  const modeEditable = isEditable("mode");
  const strategyEditable = isEditable("strategy");
  const maxTicketsEditable = isEditable("maxTicketsPerUser");
  const activeUsersOnlyEditable = isEditable("activeUsersOnly");
  const currentAssignmentTypesEditable = isEditable("currentAssignmentTypes");

  const userOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return buildUserOptions(team?.users).map((user) => ({
      id: user.id,
      type: "badge" as const,
      value: user.id,
      badge: { text: user.name },
    }));
  }, [team?.users]);

  const userGroupsLoad = useLoad(async () => {
    const client = effectApi.getClient();
    const result = await effectApi.runPromise(
      catchApiErrors(client.userGroup.listUserGroups({})),
    );
    return result.groups;
  }, [effectApi]);

  const groupOptions = useMemo(() => {
    const groups = userGroupsLoad.value ?? [];
    return groups.map((group) => ({
      id: group.id,
      type: "text" as const,
      text: group.name,
      value: group.id,
    }));
  }, [userGroupsLoad.value]);

  // Multi-select dropdown wiring
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [
    currentAssignmentTypesDropdownRef,
    setCurrentAssignmentTypesDropdownRef,
  ] = useState<HTMLButtonElement | null>(null);
  const [
    currentAssignmentTypesDropdownOpen,
    setCurrentAssignmentTypesDropdownOpen,
  ] = useState(false);

  const selectedUsers: BadgeRedoListItem<string>[] = useMemo(() => {
    if (action.mode._tag !== "specific_users") {
      return [];
    }
    const selected = new Set(action.mode.mongoUserIds);
    return userOptions.filter((opt) => selected.has(opt.value));
  }, [action.mode, userOptions]);

  const selectedCurrentAssignmentTypes: BadgeRedoListItem<AssignmentTypeOption>[] =
    useMemo(() => {
      if (action.actionType !== RuleActionType.ASSIGN_V3) {
        return [];
      }
      if (action.currentAssignmentTypes === undefined) {
        return currentAssignmentTypeOptions();
      }
      const selected = new Set(action.currentAssignmentTypes);
      return currentAssignmentTypeOptions().filter((opt) =>
        selected.has(opt.value),
      );
    }, [action]);

  const selectedModeOption = modeOptions().find(
    (opt) => opt.value === action.mode._tag,
  );
  const selectedStrategyOption = strategyOptions().find(
    (opt) => opt.value === action.strategy,
  );
  const selectedGroupOption = useMemo(() => {
    const mode = action.mode;
    if (mode._tag !== "user_group") {
      return undefined;
    }
    return groupOptions.find((opt) => opt.value === mode.userGroupId);
  }, [action.mode, groupOptions]);

  const handleModeChange = (newMode: ModeTag) => {
    if (newMode === action.mode._tag) {
      return;
    }
    const updateMode = (mode: AssignV3ActionMode) => {
      if (action.actionType === RuleActionType.ASSIGN_V3) {
        onChange({
          ...action,
          currentAssignmentTypes: defaultCurrentAssignmentTypesForMode(
            mode._tag,
          ),
          mode,
        });
        return;
      }
      onChange({ ...action, mode });
    };
    switch (newMode) {
      case "specific_users":
        updateMode({ _tag: "specific_users", mongoUserIds: [] });
        break;
      case "user_group":
        updateMode({ _tag: "user_group", userGroupId: "" });
        break;
      case "ai":
        updateMode({ _tag: "ai" });
        break;
      case "actor":
        updateMode({ _tag: "actor", assigneeTypes: ["USER"] });
        break;
      case "unassign":
        updateMode({ _tag: "unassign" });
        break;
      default:
        assertNever(newMode);
    }
  };

  const selectedActorAssigneeTypes: BadgeRedoListItem<AssignV3ActorAssigneeType>[] =
    useMemo(() => {
      if (action.mode._tag !== "actor") {
        return [];
      }
      const selected = new Set(action.mode.assigneeTypes);
      return actorAssigneeTypeOptions().filter((opt) =>
        selected.has(opt.value),
      );
    }, [action.mode]);

  const [actorDropdownButtonRef, setActorDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [actorDropdownOpen, setActorDropdownOpen] = useState(false);

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<ModeTag>
        disabled={!modeEditable}
        fitToAnchor
        label={t("Assign to")}
        options={modeOptions()}
        optionSelected={({ item }) => handleModeChange(item.value)}
        selectedItem={selectedModeOption}
      />

      {action.actionType === RuleActionType.ASSIGN_V3 && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("Assign only if assignment type is ({{count}})", {
              count: selectedCurrentAssignmentTypes.length,
            })}
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!currentAssignmentTypesEditable}
            isDropdownOpen={currentAssignmentTypesDropdownOpen}
            onClick={() =>
              currentAssignmentTypesEditable &&
              setCurrentAssignmentTypesDropdownOpen(
                !currentAssignmentTypesDropdownOpen,
              )
            }
            placeholder={t("Select assignment types...")}
            ref={setCurrentAssignmentTypesDropdownRef}
            removeItem={(item) => {
              if (
                !currentAssignmentTypesEditable ||
                action.actionType !== RuleActionType.ASSIGN_V3
              ) {
                return;
              }
              const current =
                action.currentAssignmentTypes ??
                currentAssignmentTypeOptions().map((opt) => opt.value);
              onChange({
                ...action,
                currentAssignmentTypes: serializeCurrentAssignmentTypes(
                  current.filter((t) => t !== item.value),
                ),
              });
            }}
            selectedItems={selectedCurrentAssignmentTypes}
            size="xs"
          />
          <RedoMultiselectDropdown<AssignmentTypeOption>
            dropdownAnchor={currentAssignmentTypesDropdownRef}
            dropdownOpen={currentAssignmentTypesDropdownOpen}
            fitToAnchor
            items={currentAssignmentTypeOptions()}
            selectedItems={selectedCurrentAssignmentTypes}
            selectionVariant="checkbox"
            setDropdownOpen={setCurrentAssignmentTypesDropdownOpen}
            setSelectedItems={(items) => {
              if (
                !currentAssignmentTypesEditable ||
                action.actionType !== RuleActionType.ASSIGN_V3
              ) {
                return;
              }
              onChange({
                ...action,
                currentAssignmentTypes: serializeCurrentAssignmentTypes(
                  items.map((item) => item.value),
                ),
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {action.mode._tag === "specific_users" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("Users ({{count}})", { count: selectedUsers.length })}
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!modeEditable}
            isDropdownOpen={dropdownOpen}
            onClick={() => modeEditable && setDropdownOpen(!dropdownOpen)}
            placeholder={t("Select users...")}
            ref={setDropdownButtonRef}
            removeItem={(item) => {
              if (!modeEditable || action.mode._tag !== "specific_users") {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: action.mode.mongoUserIds.filter(
                    (id) => id !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedUsers}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={dropdownOpen}
            fitToAnchor
            items={userOptions}
            selectedItems={selectedUsers}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              if (!modeEditable) {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {action.mode._tag === "user_group" && (
        <RedoSingleSelectDropdownInput<string>
          disabled={!modeEditable}
          fitToAnchor
          label={t("User group")}
          options={groupOptions}
          optionSelected={({ item }) => {
            onChange({
              ...action,
              mode: { _tag: "user_group", userGroupId: item.value },
            });
          }}
          placeholder={
            userGroupsLoad.pending
              ? t("Loading groups...")
              : t("Select a group...")
          }
          selectedItem={selectedGroupOption}
        />
      )}

      {action.mode._tag === "actor" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("Assignee types ({{count}})", {
              count: selectedActorAssigneeTypes.length,
            })}
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!modeEditable}
            isDropdownOpen={actorDropdownOpen}
            onClick={() =>
              modeEditable && setActorDropdownOpen(!actorDropdownOpen)
            }
            placeholder={t("Select assignee types...")}
            ref={setActorDropdownButtonRef}
            removeItem={(item) => {
              if (!modeEditable || action.mode._tag !== "actor") {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "actor",
                  assigneeTypes: action.mode.assigneeTypes.filter(
                    (type) => type !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedActorAssigneeTypes}
            size="xs"
          />
          <RedoMultiselectDropdown<AssignV3ActorAssigneeType>
            dropdownAnchor={actorDropdownButtonRef}
            dropdownOpen={actorDropdownOpen}
            fitToAnchor
            items={actorAssigneeTypeOptions()}
            selectedItems={selectedActorAssigneeTypes}
            selectionVariant="checkbox"
            setDropdownOpen={setActorDropdownOpen}
            setSelectedItems={(items) => {
              if (!modeEditable) {
                return;
              }
              onChange({
                ...action,
                mode: {
                  _tag: "actor",
                  assigneeTypes: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Conversation will be assigned to whoever fired the trigger when their type is in this list.",
            )}
          </Text>
        </Flex>
      )}

      {action.mode._tag !== "unassign" &&
        action.mode._tag !== "actor" &&
        action.mode._tag !== "ai" && (
          <>
            <RedoSingleSelectDropdownInput<AssignV3Strategy>
              disabled={!strategyEditable}
              fitToAnchor
              label={t("Strategy")}
              options={strategyOptions()}
              optionSelected={({ item }) => {
                onChange({ ...action, strategy: item.value });
              }}
              selectedItem={selectedStrategyOption}
            />

            <Flex dir="column" gap="xs">
              <RedoTextInput
                description={t(
                  "Optional. Users at or above this many open tickets are skipped. Leave blank for no cap.",
                )}
                inputTypeHint="number"
                label={t("Max open tickets per user")}
                min={1}
                placeholder={t("No cap")}
                setValue={(value) => {
                  const trimmed = value.trim();
                  if (trimmed === "") {
                    onChange({ ...action, maxTicketsPerUser: null });
                    return;
                  }
                  const parsed = Number.parseInt(trimmed, 10);
                  if (Number.isFinite(parsed) && parsed > 0) {
                    onChange({ ...action, maxTicketsPerUser: parsed });
                  }
                }}
                state={maxTicketsEditable ? "default" : "readonly"}
                value={
                  action.maxTicketsPerUser == null
                    ? ""
                    : String(action.maxTicketsPerUser)
                }
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <RedoCheckbox
                description={t(
                  "Users who are off shift per their work schedule are skipped. If nobody in the pool is on shift, no one is assigned.",
                )}
                disabled={!activeUsersOnlyEditable}
                label={t("Only assign to active users")}
                setValue={(value) => {
                  onChange({ ...action, activeUsersOnly: value === true });
                }}
                size="xs"
                value={action.activeUsersOnly ?? false}
              />
            </Flex>
          </>
        )}
    </Flex>
  );
});
